import { useRef, useState, useEffect } from 'react';
import { useInView } from 'framer-motion';

const QUERY = "Who's the best plumber in Denver?";

const RESPONSES = [
  { num: 1, name: 'Mile High Plumbing Co.', desc: 'Known for emergency service and upfront pricing.' },
  { num: 2, name: 'Rocky Mountain Drain Solutions', desc: 'Highly rated with 500+ Google reviews.' },
  { num: 3, name: 'Front Range Plumbing Pros', desc: 'Family-owned, serving Denver since 2012.' },
];

/* ══════════════════════════════════════════
   SECTION 6 — THE PROOF
   Live ChatGPT response with typing animation.
   This is the single most important element
   on the entire landing page.
   ══════════════════════════════════════════ */
export default function TheProof() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const started = useRef(false);
  const timers = useRef([]);

  const [queryText, setQueryText] = useState('');
  const [showCursor, setShowCursor] = useState(false);
  const [responseTexts, setResponseTexts] = useState(['', '', '']);
  const [visibleCount, setVisibleCount] = useState(0);
  const [showPunch1, setShowPunch1] = useState(false);
  const [showPunch2, setShowPunch2] = useState(false);

  const schedule = (fn, delay) => {
    const id = setTimeout(fn, delay);
    timers.current.push(id);
  };

  useEffect(() => {
    if (!isInView || started.current) return;
    started.current = true;
    setShowCursor(true);

    let t = 0;

    /* ── Phase 1: type query at 30 ms / char ── */
    for (let i = 1; i <= QUERY.length; i++) {
      schedule(() => setQueryText(QUERY.slice(0, i)), t + i * 30);
    }
    t += QUERY.length * 30 + 500; /* 500 ms pause after query */

    /* ── Phase 2: type each response at 15 ms / char ── */
    RESPONSES.forEach((resp, rIdx) => {
      const full = `${resp.name} \u2014 ${resp.desc}`;
      schedule(() => setVisibleCount(rIdx + 1), t);
      for (let i = 1; i <= full.length; i++) {
        schedule(() => {
          setResponseTexts((prev) => {
            const copy = [...prev];
            copy[rIdx] = full.slice(0, i);
            return copy;
          });
        }, t + i * 15);
      }
      t += full.length * 15 + 300; /* 300 ms between responses */
    });

    /* ── Phase 3: punchlines ── */
    t += 1200; /* extra pause → 1.5 s total (the stomach-drop) */
    schedule(() => {
      setShowCursor(false);
      setShowPunch1(true);
    }, t);
    t += 2000; /* hold 2 s */
    schedule(() => setShowPunch2(true), t);

    return () => timers.current.forEach(clearTimeout);
  }, [isInView]);

  return (
    <section ref={sectionRef} className="py-20 px-6" data-testid="the-proof-section">
      {/* Title */}
      <div className="text-center mb-12">
        <h2
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '28px',
            fontWeight: 300,
            color: '#F0EBE3',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
          data-testid="proof-title"
        >
          THIS IS WHAT AI SAYS WHEN YOUR CUSTOMER ASKS
        </h2>
        <p
          className="mt-3"
          style={{ fontSize: '14px', color: 'rgba(240,235,227,0.5)' }}
          data-testid="proof-subtitle"
        >
          Real response. Real competitors. Real problem.
        </p>
      </div>

      {/* ── The AI Response Card ── */}
      <div
        className="mx-auto"
        style={{
          maxWidth: '560px',
          backgroundColor: 'rgba(240,235,227,0.04)',
          border: '1px solid rgba(240,235,227,0.08)',
          borderRadius: '2px',
          padding: '28px',
        }}
        data-testid="ai-response-card"
      >
        {/* Header: traffic-light dots + ChatGPT */}
        <div className="flex items-center gap-1.5 mb-5">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#E5534B' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#E5A000' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#4AC26B' }} />
          <span className="ml-2" style={{ fontSize: '12px', color: 'rgba(240,235,227,0.4)' }}>
            ChatGPT
          </span>
        </div>

        {/* Query bubble */}
        {queryText && (
          <div className="flex justify-end mb-5">
            <div
              className="px-4 py-3"
              style={{
                backgroundColor: 'rgba(240,235,227,0.08)',
                borderRadius: '12px 12px 2px 12px',
              }}
            >
              <p style={{ fontSize: '14px', color: '#F0EBE3' }}>
                {queryText}
                {showCursor && (
                  <span
                    className="inline-block w-[2px] h-[14px] ml-0.5 align-middle"
                    style={{
                      backgroundColor: '#F0EBE3',
                      animation: 'blink 1s step-end infinite',
                    }}
                  />
                )}
              </p>
            </div>
          </div>
        )}

        {/* Typed responses */}
        {visibleCount > 0 && (
          <div className="space-y-4 mb-6">
            {RESPONSES.slice(0, visibleCount).map((resp, i) => {
              const typed = responseTexts[i];
              const nameLen = resp.name.length;
              const namePart = typed.slice(0, nameLen);
              const rest = typed.length > nameLen ? typed.slice(nameLen) : '';
              return (
                <div key={i} className="flex gap-3">
                  <span
                    style={{
                      color: 'rgba(240,235,227,0.5)',
                      fontSize: '14px',
                      fontWeight: 500,
                      minWidth: '20px',
                    }}
                  >
                    {resp.num}.
                  </span>
                  <div style={{ lineHeight: 1.6 }}>
                    <span style={{ color: '#F0EBE3', fontSize: '14px', fontWeight: 500 }}>
                      {namePart}
                    </span>
                    {rest && (
                      <span style={{ color: 'rgba(240,235,227,0.5)', fontSize: '12px' }}>
                        {rest}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* The punchline — the most important 6 words on the page */}
        {showPunch1 && (
          <div
            style={{ borderTop: '1px solid rgba(240,235,227,0.08)' }}
            className="pt-5 text-center"
          >
            <p
              className="italic"
              style={{ color: '#C67A5C', fontSize: '14px' }}
              data-testid="proof-punchline"
            >
              Your business isn't here.
            </p>
            {showPunch2 && (
              <p
                className="mt-2"
                style={{
                  color: '#F0EBE3',
                  fontSize: '13px',
                  animation: 'fadeInUp 0.8s ease forwards',
                }}
                data-testid="proof-punchline-2"
              >
                We're changing that.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
